import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { apiURL } from "../config/config";
import { ClipLoader } from "react-spinners";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const SELF_EDITABLE_FIELDS = ["firstName", "lastName", "nickname"];
const ADMIN_EDITABLE_FIELDS = [
  "firstName",
  "lastName",
  "nickname",
  "username",
  "level",
  "department",
  "position",
  "role",
  "team",
  "teamGroup",
  "teamRole",
  "flow",
];

const EditUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = currentUser?.role === "admin";
  const isEditingSelf = currentUser?._id === id;
  const canManageAllFields = isAdmin;

  const [userData, setUserData] = useState(null);
  const [teams, setTeams] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initLoading, setInitLoading] = useState(true);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const token = localStorage.getItem("token");
        const cachedTeams = localStorage.getItem("teams");
        const cachedDepartments = localStorage.getItem("departments");

        if (cachedTeams && cachedDepartments) {
          setTeams(JSON.parse(cachedTeams));
          setDepartments(JSON.parse(cachedDepartments));
        } else {
          const [teamsRes, departmentsRes] = await Promise.all([
            axios.get(`${apiURL}teams`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            axios.get(`${apiURL}departments`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
          ]);
          setTeams(teamsRes.data);
          setDepartments(departmentsRes.data);
          localStorage.setItem("teams", JSON.stringify(teamsRes.data));
          localStorage.setItem("departments", JSON.stringify(departmentsRes.data));
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
        toast.error("โหลดทีมหรือแผนกไม่สำเร็จ");
      } finally {
        setInitLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${apiURL}users/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setUserData({
          ...response.data,
          nickname: response.data?.nickname || "",
          password: "",
        });
      } catch (error) {
        console.error("Error fetching user:", error);
        toast.error("โหลดข้อมูลผู้ใช้ไม่สำเร็จ");
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id]);

  useEffect(() => {
    if (!canManageAllFields) {
      return;
    }

    const fetchFlows = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${apiURL}approve-flows`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setFlows(res.data.flows || res.data);
      } catch (error) {
        console.error("Error fetching flows:", error);
        toast.error("โหลดสายอนุมัติไม่สำเร็จ");
      }
    };

    fetchFlows();
  }, [canManageAllFields]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const normalizedValue =
      name === "username" ? value.replace(/\s/g, "") : value;

    setUserData((prev) => ({
      ...prev,
      [name]:
        name === "level" && normalizedValue !== ""
          ? Number(normalizedValue)
          : normalizedValue,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canManageAllFields && !isEditingSelf) {
      toast.error("คุณไม่มีสิทธิ์แก้ไขผู้ใช้นี้");
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const { _id, __v, resetToken, resetTokenExpiry, ...allowedFields } = userData;
      const editableFields = canManageAllFields
        ? ADMIN_EDITABLE_FIELDS
        : SELF_EDITABLE_FIELDS;
      const payload = editableFields.reduce((acc, field) => {
        if (allowedFields[field] !== undefined) {
          acc[field] = allowedFields[field];
        }
        return acc;
      }, {});

      if (canManageAllFields) {
        const level = Number(userData.level);
        if (!Number.isInteger(level) || level < 1 || level > 99) {
          toast.error("Level ต้องเป็นเลขจำนวนเต็มระหว่าง 1 ถึง 99");
          setSaving(false);
          return;
        }
        payload.level = level;
      }

      if (userData.password?.trim()) {
        payload.password = userData.password.trim();
      }

      await axios.patch(`${apiURL}users/${id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const loggedInUser = JSON.parse(localStorage.getItem("user"));
      if (loggedInUser && loggedInUser._id === id) {
        const updatedUser = { ...loggedInUser, ...payload };
        delete updatedUser.password;
        localStorage.setItem("user", JSON.stringify(updatedUser));
      }

      toast.success("อัปเดตข้อมูลผู้ใช้สำเร็จ");
      navigate("/users");
    } catch (error) {
      console.error("Error updating user:", error);
      toast.error("อัปเดตข้อมูลผู้ใช้ไม่สำเร็จ");
    } finally {
      setSaving(false);
    }
  };

  if (loading || initLoading || !userData) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <ClipLoader color="#6366F1" loading={true} size={50} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 px-4 sm:px-6 py-6 flex justify-center items-start transition-colors duration-300">
      <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-lg rounded-2xl w-full max-w-xl p-6 sm:p-8">
        <div className="mb-6 text-center sm:text-left">
          <h1 className="text-[26px] font-semibold text-gray-900 dark:text-gray-100 leading-tight">
            แก้ไขผู้ใช้
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
            {canManageAllFields
              ? "แก้ไขข้อมูลผู้ใช้งาน เช่น แผนก ทีม และสิทธิ์การอนุมัติ"
              : "แก้ไขข้อมูลส่วนตัวของคุณ"}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 text-sm">
          <div>
            <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
              ชื่อจริง
            </label>
            <input
              type="text"
              name="firstName"
              value={userData.firstName || ""}
              onChange={handleChange}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
            />
          </div>

          <div>
            <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
              นามสกุล
            </label>
            <input
              type="text"
              name="lastName"
              value={userData.lastName || ""}
              onChange={handleChange}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
            />
          </div>

          <div>
            <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
              ชื่อเล่น
            </label>
            <input
              type="text"
              name="nickname"
              value={userData.nickname || ""}
              onChange={handleChange}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
            />
          </div>

          {canManageAllFields && (
            <div>
              <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
                Username
              </label>
              <input
                type="email"
                name="username"
                value={userData.username || ""}
                onChange={handleChange}
                inputMode="email"
                autoComplete="email"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
              />
            </div>
          )}

          <div>
            <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
              รหัสผ่านใหม่
            </label>
            <input
              type="password"
              name="password"
              value={userData.password || ""}
              onChange={handleChange}
              autoComplete="new-password"
              placeholder="เว้นว่างไว้หากไม่ต้องการเปลี่ยนรหัสผ่าน"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
            />
          </div>

          {canManageAllFields && (
            <div>
              <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
                ตำแหน่ง
              </label>
              <input
                type="text"
                name="position"
                value={userData.position || ""}
                onChange={handleChange}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
              />
            </div>
          )}

          {canManageAllFields && (
            <div>
              <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
                บทบาท
              </label>
              <select
                name="role"
                value={userData.role || "user"}
                onChange={handleChange}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          )}

          {canManageAllFields && (
            <div>
              <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
                Level
              </label>
              <input
                type="number"
                name="level"
                min="1"
                max="99"
                step="1"
                required
                value={userData.level ?? ""}
                onChange={handleChange}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
              />
            </div>
          )}

          {canManageAllFields && (
            <div>
              <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
                แผนก
              </label>
              <select
                name="department"
                value={userData.department || ""}
                onChange={handleChange}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
              >
                <option value="">เลือกแผนก</option>
                {departments.map((dept) => (
                  <option key={dept._id} value={dept.name}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {canManageAllFields && (
            <div>
              <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
                ทีม
              </label>
              <select
                name="team"
                value={userData.team || ""}
                onChange={handleChange}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
              >
                <option value="">เลือกทีม</option>
                {[...new Set(teams.map((team) => team.group))].map((groupName) => (
                  <option key={groupName} value={groupName}>
                    {groupName}
                  </option>
                ))}
              </select>
            </div>
          )}

          {canManageAllFields && (
            <div>
              <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
                กลุ่มทีม
              </label>
              <select
                name="teamGroup"
                value={userData.teamGroup || ""}
                onChange={handleChange}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
              >
                <option value="">เลือกกลุ่มทีม</option>
                {teams.map((team) => (
                  <option key={team._id} value={team.name}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {canManageAllFields && (
            <div>
              <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
                บทบาทในทีม
              </label>
              <select
                name="teamRole"
                value={userData.teamRole || ""}
                onChange={handleChange}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
              >
                <option value="">เลือกบทบาทในทีม</option>
                <option value="member">Member</option>
                <option value="head">Head</option>
                <option value="groupHead">Group Head</option>
              </select>
            </div>
          )}

          {canManageAllFields && (
            <div>
              <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
                Approval Flow
              </label>
              <p className="text-xs text-red-500 mb-2">
                หากแก้ไข Flow ต้องแจ้งให้ผู้ใช้ Logout และ Login ใหม่เพื่อรับ Flow ล่าสุด
              </p>
              <select
                name="flow"
                value={userData.flow || ""}
                onChange={handleChange}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
              >
                <option value="">-- Select Flow --</option>
                {flows.map((flow) => (
                  <option key={flow._id} value={flow._id}>
                    {flow.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="w-full sm:w-1/2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 text-sm font-medium px-6 py-2.5 rounded-md shadow-sm transition-all duration-200"
            >
              ย้อนกลับ
            </button>

            <button
              type="submit"
              disabled={saving}
              className="w-full sm:w-1/2 bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-medium px-6 py-2.5 rounded-md shadow-sm transition-all duration-200 disabled:opacity-50"
            >
              {saving ? "กำลังบันทึก..." : "บันทึกการเปลี่ยนแปลง"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUser;
